import { useEffect, useState } from "react";
import { collection, doc, getDocs, onSnapshot } from "firebase/firestore";
import { db } from "../../../lib/firebase";
import { useAuth } from "../../../hooks/useAuth";
import { useOrgSnapshot } from "../../../hooks/useOrgSnapshot";
import { Pools, type DbPool } from "../../../lib/apiTyped";
import { useResource } from "../../../lib/useResource";
import { PageHeader } from "../../../components/AppLayout";
import { Card } from "../../../components/shared/Card";
import { Spinner } from "../../../components/shared/Spinner";
import { fmtBytes, fmtNumber } from "../../../lib/format";

// Per-model breakdown lives at usage/{poolId}/periods/{period}/members/{uid}.
type ByModelEntry = {
  provider?: string;
  model?: string;
  displayName?: string;
  credits?: number;
  inputTokens?: number;
  outputTokens?: number;
  cachedReadTokens?: number;
  cacheCreationTokens?: number;
  reasoningTokens?: number;
  requests?: number;
};

type MemberPeriodDoc = {
  creditsUsed?: number;
  requestsCount?: number;
  byModel?: Record<string, ByModelEntry>;
};

const period = () => new Date().toISOString().slice(0, 7);

// poolId == orgId in Phase 1 — pages address pool docs by orgId so the
// model can change later without touching this code.
function usePoolDoc(orgId: string | null) {
  const [pool, setPool] = useState<DbPool | null>(null);
  useEffect(() => {
    if (!orgId || !db) return;
    const ref = doc(db, "pools", orgId);
    const unsub = onSnapshot(ref, (snap) => {
      setPool(snap.exists() ? (snap.data() as DbPool) : null);
    });
    return () => unsub();
  }, [orgId]);
  return pool;
}

function useMemberPeriodDoc(orgId: string | null, uid: string | null) {
  const [usage, setUsage] = useState<MemberPeriodDoc | null>(null);
  useEffect(() => {
    if (!orgId || !uid || !db) return;
    const p = period();
    const ref = doc(
      db,
      "usage",
      orgId,
      "periods",
      p,
      "members",
      uid,
    );
    const unsub = onSnapshot(ref, (snap) => {
      setUsage(snap.exists() ? (snap.data() as MemberPeriodDoc) : {});
    });
    return () => unsub();
  }, [orgId, uid]);
  return usage;
}

export function OwnerUsage() {
  const { user } = useAuth();
  const { orgId, role } = useOrgSnapshot();
  const pool = usePoolDoc(orgId);
  const memberUsage = useMemberPeriodDoc(orgId, user?.uid ?? null);
  const [pricingDisplayNames, setPricingDisplayNames] = useState<
    Record<string, string>
  >({});
  const { loading: meLoading } = useResource(
    () => (orgId ? Pools.me(orgId) : Promise.resolve(null)),
    [orgId],
  );

  useEffect(() => {
    if (!db) return;
    let cancelled = false;
    void (async () => {
      const snap = await getDocs(collection(db, "modelPricing"));
      if (cancelled) return;
      const map: Record<string, string> = {};
      snap.docs.forEach((d) => {
        const data = d.data() as { displayName?: string };
        if (data.displayName) map[d.id] = data.displayName;
      });
      setPricingDisplayNames(map);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (meLoading || !memberUsage || !pool) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  const isExternal = role === "external";

  const personalCreditsUsed = memberUsage.creditsUsed ?? 0;
  const poolCreditsUsed = pool.creditsUsedThisMonth ?? 0;
  const poolCreditsLimit = pool.monthlyCreditsLimit ?? 0;
  const poolPct =
    poolCreditsLimit > 0
      ? Math.min(100, (poolCreditsUsed / poolCreditsLimit) * 100)
      : 0;
  const personalPct =
    poolCreditsLimit > 0
      ? Math.min(100, (personalCreditsUsed / poolCreditsLimit) * 100)
      : 0;

  const breakdown = Object.entries(memberUsage.byModel ?? {})
    .map(([modelId, entry]) => ({
      modelId,
      displayName:
        entry.displayName ?? pricingDisplayNames[modelId] ?? modelId,
      credits: entry.credits ?? 0,
      requests: entry.requests ?? 0,
    }))
    .filter((row) => row.credits > 0)
    .sort((a, b) => b.credits - a.credits);

  return (
    <>
      <PageHeader
        title="Usage"
        subtitle={
          isExternal
            ? "Tu consumo personal en este periodo. Solo ves lo tuyo."
            : "Pool compartido de la organización. Tu consumo personal cuenta contra el pool, no contra una cuota individual."
        }
      />
      {pool.killSwitch && (
        <Card className="p-5 mb-4 border-red-200 bg-red-50">
          <div className="text-sm font-semibold text-red-700">
            Pool de créditos agotado
          </div>
          <p className="text-xs text-red-700 mt-1">
            La organización ha alcanzado el límite mensual. Las llamadas al
            modelo están bloqueadas hasta que el owner amplíe el plan o
            active el cobro por exceso.
          </p>
        </Card>
      )}
      {pool.storageFull && !isExternal && (
        <Card className="p-5 mb-4 border-amber-200 bg-amber-50">
          <div className="text-sm font-semibold text-amber-800">
            Almacenamiento al límite
          </div>
          <p className="text-xs text-amber-800 mt-1">
            La organización ha alcanzado el límite de almacenamiento
            compartido. Borra archivos antiguos o aumenta el plan para
            seguir subiendo.
          </p>
        </Card>
      )}

      {!isExternal && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-4">
          <UsageBar
            title="Pool de la organización"
            pct={poolPct}
            usedLabel={`${fmtNumber(poolCreditsUsed)} / ${fmtNumber(poolCreditsLimit)} créditos`}
          />
          <Card className="p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
              Storage compartido
            </div>
            <div className="mt-2 text-2xl font-black text-ink">
              {fmtBytes(pool.storageBytesUsed ?? 0)}
            </div>
            <div className="text-xs text-ink-3 mt-0.5">
              {pool.storageBytesLimit
                ? `de ${fmtBytes(pool.storageBytesLimit)}`
                : "sin límite"}
            </div>
          </Card>
          <Card className="p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
              Rate limit
            </div>
            <div className="mt-2 text-2xl font-black text-ink">
              {fmtNumber(pool.rpmLimit ?? 0)}
            </div>
            <div className="text-xs text-ink-3 mt-0.5">requests / minuto por miembro</div>
          </Card>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <UsageBar
          title="Tu consumo este mes"
          pct={personalPct}
          usedLabel={`${fmtNumber(personalCreditsUsed)} créditos`}
          accent="ink"
        />
        <Card className="p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
            Política de exceso
          </div>
          <div className="mt-2 text-2xl font-black text-ink">
            {pool.overagePolicy === "billed" ? "Cobrado" : "Bloqueado"}
          </div>
          <div className="text-xs text-ink-3 mt-0.5">
            {pool.overagePolicy === "billed"
              ? `Si el pool se agota, el exceso se factura a ${pool.overageEurPerMillionCredits ?? 0} € por millón de créditos.`
              : "Si el pool se agota, el servicio se corta hasta el siguiente periodo o ampliación."}
          </div>
        </Card>
      </div>

      {breakdown.length > 0 && (
        <Card className="p-5 mt-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-3 mb-3">
            Tu consumo por modelo
          </div>
          <div className="space-y-2">
            {breakdown.map((row) => {
              const pct =
                personalCreditsUsed > 0
                  ? (row.credits / personalCreditsUsed) * 100
                  : 0;
              return (
                <div key={row.modelId}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-ink">{row.displayName}</span>
                    <span className="text-ink-3">
                      {pct.toFixed(1)}%
                      <span className="ml-2 text-xs">({row.requests} req)</span>
                    </span>
                  </div>
                  <div
                    className="mt-1 h-1 rounded-full overflow-hidden"
                    style={{ backgroundColor: "rgba(0,0,0,0.06)" }}
                  >
                    <div
                      className="h-full transition-all"
                      style={{ width: `${pct}%`, backgroundColor: "#0f0f0f" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </>
  );
}

function UsageBar({
  title,
  pct,
  usedLabel,
  accent = "danger",
}: {
  title: string;
  pct: number;
  usedLabel: string;
  accent?: "danger" | "ink";
}) {
  const danger = accent === "danger" && pct > 90;
  return (
    <Card className="p-5">
      <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
        {title}
      </div>
      <div className="mt-2 text-2xl font-black text-ink">{pct.toFixed(1)}%</div>
      <div className="text-xs text-ink-3 mt-0.5">{usedLabel}</div>
      <div
        className="mt-3 h-1.5 rounded-full overflow-hidden"
        style={{ backgroundColor: "rgba(0,0,0,0.06)" }}
      >
        <div
          className="h-full transition-all"
          style={{
            width: `${pct}%`,
            backgroundColor: danger ? "#C61418" : "#0f0f0f",
          }}
        />
      </div>
    </Card>
  );
}
